import { useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { Strip } from '@/components/Strip'
import { GridTable } from '@/components/GridTable'
import { ProcedureStep, WizardButtons, KeyValueGrid, KeyValueGridKey } from '@/components/WizardParts'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { buildRestoreInput } from '@/layers/restore-wizard/buildRestoreInput'
import { PUSH_OUTCOME_CHIP, TRIGGER_LABEL, formatJobTime } from '@/screens/shared/jobFormatting'
import type { Problem } from '@/data/apiClient'
import type { RestoreWizardConfig, RestorePlan, WizardStage } from '@/layers/restore-wizard/types'

/**
 * The `plan` role's shared pane (`repo-wz-3`/…/`ref-wz-4`/…): "**What this restore will do.**"
 * `restoreCreatePlan` computes the write-order summary **with no read of the destination** — "this
 * is what the restore will attempt; the destination is read only as each object is written." The
 * plan is never stored and never re-derived client-side; `plan_digest` is passed forward as-is to
 * the confirmation and the trigger.
 *
 * **`tools`/`strip` added W158** — a real "Recompute plan" `icon-button` in the "Write order"
 * panel's own header (re-fires the same idempotent `restoreCreatePlan` call this pane already makes
 * on mount; the plan is stateless by design, so recomputing it is always safe) closes the spec's own
 * `tools` row; the already-rendered refusals/secured-values/warnings block, previously bare `Note`s
 * at the top level, is now wrapped in the shared `Strip` component, matching `ci-wz-3`'s own "strip
 * in pane" — no new data, just the correct wrapper on data this pane already had.
 *
 * **Top-level shape corrected to `grid2` (two EVEN columns), not `wizsplit`.** This role's own
 * prototype pane (`repo-wz-3`/`ref-wz-4`/…) has no `wizh`/`wizp` intro at all — its left column
 * opens directly with the "What this restore will do" panel header, and its right column is a
 * small "Restore point" summary — unlike every other role here, which really does open with a
 * heading and prose in a 1fr/420px `wizsplit`. Previously wrapped in `RestoreWizardLayer`'s own
 * blanket `wizsplit`/420px card, same shape mismatch `SelectRestorePointPane`'s own fix (this
 * session) found and fixed first. The "Restore point" panel shows only what this shared, possibly
 * multi-repository pane actually has (`stage.restorePoint`) — not the prototype's own per-repository
 * "4 commits on main, plus 5 refs" line, which has no equivalent field for a multi-repository or
 * whole-workspace selection and would be invented here.
 *
 * **Safety-guarantee `Note` added (OI-C205).** `design/18-restore.md` §18.6's replacement claim
 * ("No restore ever deletes anything...") was only ever rendered on `v-restore`'s launcher screen
 * and again in `m-stepup`'s own confirmation copy (`M_STEPUP_COPY.scopeNoteTemplate`) right before
 * the write — so a direct wizard entry (skipping `v-restore`) was never actually left with zero
 * visibility, `m-stepup` is reached either way. This pane sat in between with neither sentence,
 * the one intermediate screen a customer evaluating "what will this restore actually do" never saw
 * it on — restated here verbatim (`design/18-restore.md:482`), unconditionally, not gated on
 * `plan.data` loading.
 */
export function PlanPane({
  config,
  stage,
  onNext,
  onBack,
}: {
  config: RestoreWizardConfig
  stage: Extract<WizardStage, { stage: 'plan' }>
  onNext: (plan: RestorePlan) => void
  onBack: () => void
}) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!

  const plan = useMutation({
    mutationFn: async (): Promise<RestorePlan> => {
      const client = getApiClient()
      const { data, error } = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/restore-plans', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: buildRestoreInput(config, stage),
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as RestorePlan
    },
  })

  useEffect(() => {
    plan.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const problem = plan.error instanceof ApiProblemError ? plan.error.problem : undefined

  return (
    <div data-scr={brandUnitId(config.unitIds.plan)} data-cmp="pane">
      <Note>
        No restore ever deletes anything, and no restore ever overwrites code or history. Where a restore replaces a policy value, the
        previous value is recorded on the report so it can be put back.
      </Note>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      {plan.data && (
        <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(2, 1fr)', alignItems: 'start' }}>
          <div>
            <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-6">
              <div data-cmp="panel-header" className="p-5 border-b border-bd font-semibold text-body-lg flex items-center gap-4">
                <span>Write order</span>
                <div data-cmp="tools" className="flex-1 flex items-center justify-end gap-3 min-w-0">
                  <Note>This is what the restore will attempt; the destination is read only as each object is written.</Note>
                  <IconButton data-act={undefined} icon="i-refresh" label="Recompute plan" disabled={plan.isPending} onClick={() => plan.mutate()} />
                </div>
              </div>
              {plan.data.phases.map((phase) => (
                <ProcedureStep key={phase.order} ordinal={String(phase.order)} boxed>
                  <b>{phase.object_type}</b> — {phase.action_expected}
                  <span className="ml-3">
                    <Chip variant="plain">{phase.count}</Chip>
                    {phase.protection_write && <Chip variant="off">protection write</Chip>}
                    {phase.starts_builds && <Chip variant="off">starts builds</Chip>}
                    {phase.write_observed === false && <Note>Not yet observed against the platform — not described as verified.</Note>}
                  </span>
                </ProcedureStep>
              ))}
            </div>

            {plan.data.pushes && plan.data.pushes.length > 0 && (
              <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-6">
                <div data-cmp="panel-header" className="p-5 border-b border-bd font-semibold text-body-lg">
                  Push estimate
                </div>
                <GridTable
                  columns={[
                    { key: 'repository', header: 'Repository', render: (push) => <span className="font-mono">{push.slug}</span> },
                    { key: 'marginal', header: 'Marginal pack', render: (push) => push.marginal_bytes.display },
                    { key: 'pushes', header: 'Pushes', render: (push) => push.push_count || '—' },
                    { key: 'plan', header: 'Plan', render: (push) => <Chip variant={PUSH_OUTCOME_CHIP[push.outcome]}>{push.display ?? push.outcome}</Chip> },
                  ]}
                  rows={plan.data.pushes}
                  getRowKey={(push) => push.repository_id}
                />
              </div>
            )}

            {(plan.data.refusals_expected.length > 0 ||
              (plan.data.secured_values_expected && (plan.data.secured_values_expected.count ?? 0) > 0) ||
              (plan.data.warnings ?? []).length > 0) && (
              <Strip>
                {plan.data.refusals_expected.length > 0 && (
                  <div className="mb-3 last:mb-0">
                    <Note>Refused before anything is attempted, not failures:</Note>
                    {plan.data.refusals_expected.map((r) => (
                      <Note key={r.object_type}>{r.display}</Note>
                    ))}
                  </div>
                )}

                {plan.data.secured_values_expected && (plan.data.secured_values_expected.count ?? 0) > 0 && (
                  <Note>{plan.data.secured_values_expected.display ?? `${plan.data.secured_values_expected.count} secured values will be created as shells with no value.`}</Note>
                )}

                {(plan.data.warnings ?? []).map((w, i) => (
                  <Note key={i}>{w}</Note>
                ))}
              </Strip>
            )}
          </div>

          <div>
            <div data-cmp="panel-header" className="font-semibold text-body-lg mb-4">
              Restore point
            </div>
            <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-6 p-5">
              <KeyValueGrid>
                <KeyValueGridKey>Job taken</KeyValueGridKey>
                <div className="font-mono">
                  {stage.restorePoint.data_snapshot.handle} ·{' '}
                  {stage.restorePoint.started_at ? stage.restorePoint.started_at.display : formatJobTime(stage.restorePoint.data_snapshot.started_at)}
                </div>
                <KeyValueGridKey>Trigger</KeyValueGridKey>
                <div>{TRIGGER_LABEL[stage.restorePoint.data_snapshot.trigger] ?? stage.restorePoint.data_snapshot.trigger}</div>
                <KeyValueGridKey>Configuration</KeyValueGridKey>
                <div className="font-mono">
                  {stage.restorePoint.configuration_snapshot
                    ? `${stage.restorePoint.configuration_snapshot.handle} · ${formatJobTime(stage.restorePoint.configuration_snapshot.started_at)}`
                    : 'Not captured at or before this point'}
                </div>
              </KeyValueGrid>
            </div>
          </div>
        </div>
      )}

      {/* Always reachable, even before the plan resolves or if it errors — `plan.data &&` above
       * gates the two-column content, not the ability to go Back. */}
      <WizardButtons>
        <Button data-act={actionId(`nav.${config.unitIds.targets}`)} ghost onClick={onBack}>
          Back
        </Button>
        <Button data-act={actionId('restore.plan')} disabled={!plan.data || plan.isPending} onClick={() => plan.data && onNext(plan.data)}>
          Next: confirm
        </Button>
      </WizardButtons>
    </div>
  )
}
